import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import QRCode from 'react-qr-code';
import { FaWhatsapp } from 'react-icons/fa';
import { FiCheck, FiChevronRight } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { AUTH_API_URL } from '../config';
import WorkspaceDialog from './WorkspaceDialog';
import useWhatsAppLink from './useWhatsAppLink';

const WhatsAppContext = createContext({ linked: null, phone: '', openWhatsApp: () => {} });
export const useWhatsAppAccess = () => useContext(WhatsAppContext);

export function WhatsAppProvider({ children, enabled = true }) {
  const { t } = useTranslation('workspace');
  const [open, setOpen] = useState(false);
  const [linked, setLinked] = useState(null);
  const [phone, setPhone] = useState('');
  const [error, setError] = useState(false);
  const request = useRef(0);
  const token = localStorage.getItem('token');
  const refresh = useCallback(async () => {
    if (!enabled || !token) return;
    const current = ++request.current;
    try {
      const { data } = await axios.get(`${AUTH_API_URL}/profile`, { headers: { Authorization: `Bearer ${token}` } });
      if (current !== request.current) return;
      if (typeof data.linked_channels?.whatsapp !== 'boolean') throw new Error('Missing channel status');
      setLinked(data.linked_channels.whatsapp);
      setPhone(data.phone_e164 || '');
      setError(false);
    } catch {
      if (current === request.current) setError(true);
    }
  }, [token, enabled]);

  useEffect(() => {
    refresh();
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      request.current += 1;
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh]);
  useEffect(() => {
    if (!open || linked === true) return;
    const timer = setInterval(() => { if (document.visibilityState === 'visible') refresh(); }, 5000);
    return () => clearInterval(timer);
  }, [open, linked, refresh]);

  const openWhatsApp = () => { setOpen(true); refresh(); };
  return <WhatsAppContext.Provider value={{ linked, phone, openWhatsApp }}>
    {children}
    {open && <WorkspaceDialog title={t('whatsappTitle')} onClose={() => { setOpen(false); refresh(); }} className="ws-whatsapp-dialog">
      {!enabled ? <p role="status">{t('whatsappLocalUnavailable')}</p> : linked === null ? <div className="ws-whatsapp-panel">
        {error ? <div role="alert"><p>{t('loadError')}</p><button className="ws-secondary" onClick={refresh}>{t('retry')}</button></div> : <p role="status">{t('loading')}</p>}
      </div> : <WhatsAppPanel linked={linked} />}
    </WorkspaceDialog>}
  </WhatsAppContext.Provider>;
}

export function WhatsAppInvitation() {
  const { linked, openWhatsApp } = useWhatsAppAccess();
  const { t } = useTranslation('workspace');
  if (linked !== false) return null;
  return <button type="button" className="ws-whatsapp-invitation" onClick={openWhatsApp} aria-haspopup="dialog">
    <FaWhatsapp aria-hidden="true" /><span><strong>{t('whatsappTitle')}</strong><span>{t('whatsappConnect')}</span></span><FiChevronRight aria-hidden="true" />
  </button>;
}

function WhatsAppPanel({ linked }) {
  const { t } = useTranslation('workspace');
  const { whatsappUrl, qrCodeBase64, linkingCode, loading, error, timeRemaining, isMobile, generateLink, formatTime } = useWhatsAppLink();
  // A connected account opens the bot without sending another linking code.
  const url = linked ? whatsappUrl.split('?')[0] : whatsappUrl;
  const expired = !linked && timeRemaining <= 0;
  return <div className="ws-whatsapp-panel">
    {linked ? <p className="ws-whatsapp-connected" role="status"><FiCheck aria-hidden="true" />{t('whatsappConnected')}</p> : <p>{t('whatsappIntro')}</p>}
    {loading ? <p role="status">{t('loading')}</p> : error ? <div role="alert"><p>{t('whatsappError')}</p><button className="ws-secondary" onClick={generateLink}>{t('retry')}</button></div> : url && !expired ? <>
      {!isMobile && <div className="ws-whatsapp-qr">
        {!linked && qrCodeBase64 ? <img src={qrCodeBase64} alt={t('whatsappQr')} /> : <QRCode value={url} size={192} title={t('whatsappQr')} />}
        <p>{t('whatsappScan')}</p>
      </div>}
      <a className="ws-primary ws-whatsapp-open" href={url} target="_blank" rel="noopener noreferrer"><FaWhatsapp aria-hidden="true" />{t('whatsappOpen')}</a>
      {!linked && <>
        <p className="ws-whatsapp-instructions">{t('whatsappSendCode')}</p>
        <div className="ws-whatsapp-code"><span>{t('whatsappCode')}</span><strong>{linkingCode}</strong><small>{t('whatsappExpires', { time: formatTime(timeRemaining) })}</small></div>
      </>}
    </> : <><p role="status">{t(expired && whatsappUrl ? 'whatsappExpired' : 'whatsappError')}</p><button className="ws-secondary" onClick={generateLink}>{t('whatsappRenew')}</button></>}
  </div>;
}
